import React, { useState, useCallback } from 'react';
import api from '../services/api';
import ConfigDashboard from '../components/SystemSettings/ConfigDashboard';
import NotificationPolicyMatrix from '../components/SystemSettings/NotificationPolicyMatrix';
import ConfigForm from '../components/SystemSettings/ConfigForm';
import ConfirmModal from '../components/UI/ConfirmModal';
import BasicConfigTab from '../components/SystemSettings/tabs/BasicConfigTab';
import QualificationsTab from '../components/SystemSettings/tabs/QualificationsTab';
import LeaveTab from '../components/SystemSettings/tabs/LeaveTab';
import PayrollTab from '../components/SystemSettings/tabs/PayrollTab';
import BankFilesTab from '../components/SystemSettings/tabs/BankFilesTab';
import PoliciesTab from '../components/SystemSettings/tabs/PoliciesTab';
import ComplianceTab from '../components/SystemSettings/tabs/ComplianceTab';
import SystemAdminTab from '../components/SystemSettings/tabs/SystemAdminTab';
import { useTabPersistence, usePersistence } from '../hooks/usePersistence';
import { useSystemConfig, useSystemConfigMutations } from '../hooks/useSystemConfig';
import { getCategoryTitle, getCategoryEndpoint } from '../services/configDataService';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import {
  LayoutDashboard, Building2, GraduationCap, CalendarDays,
  Banknote, Landmark, ClipboardList, Scale, Settings, Bell,
  ChevronsUp, ChevronsDown, X
} from 'lucide-react';

// ─── Tab registry (defined outside component — stable reference) ──────────────
const TABS = [
  { id: 'dashboard',     name: 'Dashboard',        Icon: LayoutDashboard },
  { id: 'basic',         name: 'Basic Config',      Icon: Building2 },
  { id: 'qualifications',name: 'Qualifications',    Icon: GraduationCap },
  { id: 'leave',         name: 'Leave & Holidays',  Icon: CalendarDays },
  { id: 'payroll',       name: 'Payroll & Benefits',Icon: Banknote },
  { id: 'bank-files',    name: 'Bank Files',        Icon: Landmark },
  { id: 'policies',      name: 'Work Policies',     Icon: ClipboardList },
  { id: 'compliance',    name: 'Compliance',        Icon: Scale },
  { id: 'system',        name: 'System Admin',      Icon: Settings },
  { id: 'notifications', name: 'Notifications',     Icon: Bell },
];
const VALID_TABS = TABS.map((t) => t.id);

// ─── Component ────────────────────────────────────────────────────────────────
const SystemSettings = ({ initialTab = 'dashboard' }) => {
  // ── Data ──────────────────────────────────────────────────────────────────
  const { configData, isLoading, isError, error, refetch } = useSystemConfig();
  const { addItem, updateItem, removeItem } = useSystemConfigMutations();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [confirmAction, setConfirmAction] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formCategory, setFormCategory] = useState('');
  const [formItem, setFormItem] = useState(null);
  const [formTitle, setFormTitle] = useState('');

  // ── Persistence ───────────────────────────────────────────────────────────
  const getInitialTab = () => {
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    return tabParam || initialTab;
  };
  const [activeTab, setActiveTab] = useTabPersistence('system-settings', getInitialTab(), VALID_TABS);
  const [searchTerm, setSearchTerm] = usePersistence('system-settings-searchTerm', '');
  const [globalCollapsed, setGlobalCollapsed] = usePersistence('system-settings-globalCollapsed', {});

  // ── CRUD handlers ─────────────────────────────────────────────────────────
  const handleAddNew = useCallback((category) => {
    setFormCategory(category);
    setFormItem(null);
    setFormTitle(getCategoryTitle(category));
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((category, item) => {
    setFormCategory(category);
    setFormItem(item);
    setFormTitle(getCategoryTitle(category));
    setShowForm(true);
  }, []);

  const handleDelete = useCallback((category, item) => {
    setConfirmAction({ category, item });
  }, []);

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    const { category, item } = confirmAction;
    try {
      const endpoint = getCategoryEndpoint(category);
      await api.delete(`/configuration/${endpoint}/${item.id}/`);
      removeItem(category, item.id);
    } catch (err) {
      console.error('Error deleting item:', err);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleFormSave = useCallback((savedItem) => {
    if (formItem) {
      updateItem(formCategory, savedItem);
    } else {
      addItem(formCategory, savedItem);
    }
  }, [formItem, formCategory, addItem, updateItem]);

  const handleFormClose = useCallback(() => {
    setShowForm(false);
    setFormCategory('');
    setFormItem(null);
    setFormTitle('');
  }, []);

  // ── Shared ConfigCard props ───────────────────────────────────────────────
  const cardProps = {
    searchTerm, setSearchTerm,
    globalCollapsed, setGlobalCollapsed,
    onAddNew: handleAddNew,
    onEdit: handleEdit,
    onDelete: handleDelete,
  };

  // ── Global search ─────────────────────────────────────────────────────────
  const SEARCH_CATEGORIES = {
    departments:    { title: 'Departments',    tab: 'basic',          fields: ['description'] },
    locations:      { title: 'Locations',      tab: 'basic',          fields: ['address', 'timezone'] },
    classifications:{ title: 'Classifications',tab: 'basic',          fields: ['description'] },
    designations:   { title: 'Designations',   tab: 'basic',          fields: ['description'] },
    employmentTypes:{ title: 'Employment Types',tab: 'basic',         fields: ['description'] },
    payGrades:      { title: 'Pay Grades',     tab: 'basic',          fields: ['salary_min', 'salary_max'] },
    leaveTypes:     { title: 'Leave Types',    tab: 'leave',          fields: ['leave_type_statutory_days', 'leave_type_is_paid'] },
    publicHolidays: { title: 'Public Holidays',tab: 'leave',          fields: ['holiday_date', 'holiday_type'] },
    momWorkingDaysCalendar: { title: 'MOM Working Days Calendar', tab: 'leave', fields: ['year', 'calendar_month'] },
    salaryStructures:{ title: 'Salary Structures', tab: 'payroll',    fields: ['component_type'] },
    cpfRates:       { title: 'CPF Rates',      tab: 'payroll',        fields: ['age_bracket', 'employee_rate', 'employer_rate'] },
    workweekPolicies:{ title: 'Workweek Policies', tab: 'policies',   fields: ['hours_per_week'] },
    workScheduleTemplates: { title: 'Work Schedule Templates', tab: 'policies', fields: ['schedule_type_display', 'start_time', 'end_time'] },
    overtimePolicies:{ title: 'Overtime Policies', tab: 'policies',   fields: ['multiplier', 'daily_limit_hours'] },
    noticePolicies: { title: 'Notice Policies', tab: 'policies',      fields: ['service_length_min', 'notice_days'] },
    retirementPolicies: { title: 'Retirement Policies', tab: 'policies', fields: ['retirement_age', 'reemployment_age'] },
    foreignWorkerLevies: { title: 'Foreign Worker Levies', tab: 'compliance', fields: ['pass_type', 'sector', 'levy_rate_monthly'] },
    wicaCompensations: { title: 'WICA Compensations', tab: 'compliance', fields: ['injury_type', 'compensation_cap'] },
    systemSettings: { title: 'System Settings', tab: 'system',        fields: ['value'] },
    integrationKeys:{ title: 'Integration Keys', tab: 'system',       fields: ['key_type', 'expires_at'] },
    customFields:   { title: 'Custom Fields',  tab: 'system',         fields: ['model_name', 'field_type', 'required'] },
    institutionTypes: { title: 'Institution Types', tab: 'qualifications', fields: ['description'] },
    qualificationLevels: { title: 'Qualification Levels', tab: 'qualifications', fields: ['level_number', 'country', 'description'] },
    fieldsOfStudy:  { title: 'Fields of Study', tab: 'qualifications', fields: ['category', 'description'] },
    certificationAuthorities: { title: 'Certification Authorities', tab: 'qualifications', fields: ['short_name', 'country'] },
    certificationCategories: { title: 'Certification Categories', tab: 'qualifications', fields: ['description', 'industry_focus'] },
    certificationTemplates: { title: 'Certification Templates', tab: 'qualifications', fields: ['short_name', 'validity_period_months'] },
  };

  const getGlobalSearchResults = () => {
    if (!searchTerm) return [];
    const lower = searchTerm.toLowerCase();
    const results = [];
    Object.entries(configData).forEach(([category, items]) => {
      const info = SEARCH_CATEGORIES[category];
      if (!info || !Array.isArray(items)) return;
      items.forEach((item) => {
        const text = [
          item.name, item.department_name, item.location_name,
          item.key, item.holiday_name, item.leave_type_name,
          ...info.fields.map((f) => item[f]),
        ].filter(Boolean).join(' ').toLowerCase();
        if (text.includes(lower)) {
          results.push({
            ...item, category,
            categoryTitle: info.title, categoryTab: info.tab,
            displayName: item.name || item.department_name || item.location_name || item.key || `${info.title} Item`,
            fields: info.fields,
          });
        }
      });
    });
    return results;
  };

  const renderGlobalSearchResults = () => {
    const results = getGlobalSearchResults();
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Search Results for "{searchTerm}"</h2>
              <p className="text-sm text-gray-600 mt-1">
                Found {results.length} {results.length === 1 ? 'item' : 'items'} across {new Set(results.map((r) => r.category)).size} categories
              </p>
            </div>
            <button onClick={() => setSearchTerm('')} className="btn btn-admin-outline">Clear Search</button>
          </div>
        </div>
        {results.length > 0 ? (
          <div className="space-y-4">
            {Object.entries(
              results.reduce((acc, r) => {
                if (!acc[r.categoryTitle]) acc[r.categoryTitle] = [];
                acc[r.categoryTitle].push(r);
                return acc;
              }, {})
            ).map(([categoryTitle, categoryResults]) => (
              <div key={categoryTitle} className="bg-white rounded-2xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">{categoryTitle}</h3>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-500">{categoryResults.length} {categoryResults.length === 1 ? 'result' : 'results'}</span>
                      <button
                        onClick={() => { setSearchTerm(''); setActiveTab(categoryResults[0].categoryTab); }}
                        className="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1.5 rounded-full hover:bg-red-50 transition-colors"
                      >
                        View All →
                      </button>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  {categoryResults.map((result) => (
                    <div key={`${result.category}-${result.id}`} className="px-6 py-4 border border-gray-200 rounded-xl table-row group">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{result.displayName}</p>
                          <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {result.fields.map((field) => result[field] && (
                              <div key={field} className="text-xs text-gray-600">
                                <span className="font-medium text-gray-700 capitalize">{field.replace(/_/g, ' ')}:</span>{' '}
                                <span className="text-gray-900">{typeof result[field] === 'boolean' ? (result[field] ? 'Yes' : 'No') : result[field]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleEdit(result.category, result)} className="icon-button-admin" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button onClick={() => { setSearchTerm(''); setActiveTab(result.categoryTab); }} className="icon-button-admin" title="Go to tab">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No results found</h3>
            <p className="mt-2 text-sm text-gray-500">Try adjusting your search terms or browse through the categories above.</p>
            <button onClick={() => setSearchTerm('')} className="btn btn-admin-outline mt-4">Clear Search</button>
          </div>
        )}
      </div>
    );
  };

  // ── Tab content ───────────────────────────────────────────────────────────
  const renderTabContent = () => {
    if (
      searchTerm
      && activeTab !== 'dashboard'
      && activeTab !== 'bank-files'
    ) {
      return renderGlobalSearchResults();
    }

    switch (activeTab) {
      case 'dashboard':
        return <ConfigDashboard configData={configData} onNavigateToTab={setActiveTab} />;
      case 'basic':
        return <BasicConfigTab configData={configData} cardProps={cardProps} />;
      case 'qualifications':
        return <QualificationsTab configData={configData} cardProps={cardProps} />;
      case 'leave':
        return <LeaveTab configData={configData} cardProps={cardProps} />;
      case 'payroll':
        return <PayrollTab configData={configData} cardProps={cardProps} />;
      case 'bank-files':
        return <BankFilesTab />;
      case 'policies':
        return <PoliciesTab configData={configData} cardProps={cardProps} />;
      case 'compliance':
        return <ComplianceTab configData={configData} cardProps={cardProps} />;
      case 'system':
        return <SystemAdminTab configData={configData} cardProps={cardProps} />;
      case 'notifications':
        return <NotificationPolicyMatrix />;
      default:
        return null;
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div>
        <div className="mb-6 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-6">
          {TABS.map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-9 bg-gray-200 rounded-full w-28"></div>
            </div>
          ))}
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white p-6 rounded-2xl shadow border">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="ml-4 flex-1">
                    <div className="h-4 bg-gray-200 rounded-xl w-3/4 mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded-xl w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-white rounded-2xl shadow border">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gray-200 rounded-xl"></div>
                    <div className="h-5 bg-gray-200 rounded-xl w-32"></div>
                  </div>
                  <div className="h-8 bg-gray-200 rounded-full w-24"></div>
                </div>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="h-16 bg-gray-200 rounded-2xl"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="fixed bottom-6 right-6 bg-white rounded-2xl shadow-lg border p-4 flex items-center space-x-3 pb-safe">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-sm font-medium text-gray-700">Loading configuration data...</span>
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (isError) {
    const message = error?.message?.includes('Authentication')
      ? error.message
      : 'Failed to load configuration data. Please check your connection and try again.';
    return (
      <div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{message}</p>
              <button onClick={() => refetch()} className="mt-2 text-sm text-red-800 underline hover:text-red-900">
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Header copy ───────────────────────────────────────────────────────────
  const headerContent = initialTab === 'basic'
    ? { title: 'Department Management', description: 'Manage organizational structure, departments, locations, and basic HR configurations' }
    : { title: 'System Settings', description: 'Comprehensive HR system configuration and settings management' };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">{headerContent.title}</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">{headerContent.description}</p>
      </div>

      {activeTab !== 'dashboard' && activeTab !== 'bank-files' && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search all configurations..."
            className="max-w-xl"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setGlobalCollapsed((prev) => {
                const allCollapsed = Object.keys(prev).length === 0 || !Object.values(prev).every(Boolean);
                const next = {};
                Object.keys(configData).forEach((k) => { next[k] = allCollapsed; });
                return next;
              })}
              className="icon-button-admin"
              title={Object.values(globalCollapsed).every(Boolean) ? 'Expand All' : 'Collapse All'}
              aria-label={Object.values(globalCollapsed).every(Boolean) ? 'Expand All' : 'Collapse All'}
            >
              {Object.values(globalCollapsed).every(Boolean) ? <ChevronsDown className="w-4 h-4" /> : <ChevronsUp className="w-4 h-4" />}
            </button>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="icon-button-admin"
                title="Clear Search"
                aria-label="Clear Search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`tab-btn-admin ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.Icon className="w-4 h-4 flex-shrink-0" />
            {tab.name}
          </button>
        ))}
      </div>

      <div className="tab-content">{renderTabContent()}</div>

      <ConfigForm
        category={formCategory}
        item={formItem}
        isOpen={showForm}
        onClose={handleFormClose}
        onSave={handleFormSave}
        title={formTitle}
        configData={configData}
      />

      <ConfirmModal
        isOpen={!!confirmAction}
        title={`Delete ${confirmAction ? getCategoryTitle(confirmAction.category) : 'Item'}`}
        message={`Are you sure you want to delete this ${confirmAction ? getCategoryTitle(confirmAction.category).toLowerCase() : 'item'}?`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="admin"
      />
    </div>
  );
};

export default SystemSettings;
